import React, { useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import MerchantAccountStore from "../MerchantAccountStore";
import { observer } from "mobx-react";
import { MerchantCard } from "./Merchant.styles";
//import MindMePaymentsLogo from "../../../../../../images/logos/mindme-payment-logo.svg";
import ellipsisVertical from "../../../../../../images/common/ellipsis-vertical-royal-blue.svg";
import { Navigate, NavigateFunction } from "react-router-dom";
import {
  AuthService,
  CustomButton,
  CustomLabel,
  FlexContainer,
  Loader,
  RedButton,
  SapphireBlueButton,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { MerchantAccount, SelectedItemModel } from "../../../shared/models";
import { ApplicationStatus } from "../../../shared/enums";
import { IAPIDeleteMerchantAccount } from "../../../shared/interfaces";
import { MerchantAccountService } from "../../../shared/services";
import { ItemsStore, RolesAndPermissionStore } from "../../../shared/store";
import { AccountSettingsStore } from "../../../shared/store";
import DeleteMerchantAccountPopUp from "./DeleteMerchantAccountPopUp";
import TickIcon from "../../../../../../images/common/check-fill-green.svg";

type IProps = {
  merchantAccountStore: MerchantAccountStore;
  navigate: NavigateFunction;
  rolesAndPermissionStore: RolesAndPermissionStore;
  itemStore: ItemsStore;
};
interface MenuItem {
  label: string;
  action: (item: MerchantAccount) => void;
}

const MerchantAccounts: React.FC<IProps> = ({
  merchantAccountStore,
  navigate,
  rolesAndPermissionStore,
  itemStore,
}) => {
  const [showDeletePopup, setShowDeletePopup] = React.useState(false);
  const [merchantAccountToDelete, setMerchantAccountToDelete] =
    React.useState<MerchantAccount | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await merchantAccountStore.fetchMerchantAccounts();
      setIsLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    itemStore?.setBreadCrumbData(
      new SelectedItemModel({
        route: "/app/merchant-accounts",
        title: "",
      }),
    );

    return () => {
      itemStore?.setBreadCrumbData();
    };
  }, []);

  const merchantAccountsData: MerchantAccount[] =
    merchantAccountStore.merchantAccounts;

  if (
    merchantAccountsData.length === 1 &&
    AuthService.hasAccount &&
    merchantAccountsData[0].applicationStatus === ApplicationStatus.Pending &&
    merchantAccountsData[0].isFiservMerchantAccount()
  ) {
    navigate(`/app/merchant-accounts/success/${merchantAccountsData[0].id}`);
  }

  useEffect(() => {
    merchantAccountStore.fetchMerchantAccounts();
  }, []);

  const getMenuItems = (item: MerchantAccount): MenuItem[] => {
    const commonItem: MenuItem[] = [
      {
        label: "Delete Account",
        action: (account: MerchantAccount) => handleDelete(account),
      },
    ];

    switch (item.applicationStatus) {
      case ApplicationStatus.Pending: {
        const menuItems: MenuItem[] = [];

        if (item.isFiservMerchantAccount()) {
          menuItems.push({
            label: "Complete Application",
            action: (account: MerchantAccount) =>
              handleCompleteApplication(account),
          });
        }

        menuItems.push({
          label: "Cancel Application",
          action: (account: MerchantAccount) =>
            handleMerchantAccountAction(account, "cancel"),
        });

        return menuItems;
      }
      case ApplicationStatus.Active:
        return [
          ...(!item.isDefault
            ? [
                {
                  label: "Set as default",
                  action: (account: MerchantAccount) =>
                    merchantAccountStore.setDefaultMerchantAccount(account.id),
                },
              ]
            : []),
          {
            label: "Make Inactive",
            action: (account: MerchantAccount) =>
              handleInactiveApplication(account),
          },
          ...commonItem,
        ];
      case ApplicationStatus.Inactive:
        return [
          {
            label: "Make Active",
            action: (account: MerchantAccount) =>
              handleActiveApplication(account),
          },
          ...commonItem,
        ];
      default:
        return commonItem;
    }
  };

  const handleDelete = (item: MerchantAccount) => {
    setShowDeletePopup(true);
    setMerchantAccountToDelete(item);
  };

  const onCloseDeletePopUp = (deleted: boolean) => {
    setShowDeletePopup(false);
    setMerchantAccountToDelete(null);
    if (deleted) {
      merchantAccountStore.fetchMerchantAccounts();
    }
  };

  const handleCompleteApplication = (item: MerchantAccount) => {
    if (
      !rolesAndPermissionStore.hasManageMerchantAccountPermission(
        "Complete the Application",
      )
    )
      return;
    merchantAccountStore.fetchCompleteApplicationLink(item.applicationId);
  };

  const handleInactiveApplication = async (item: MerchantAccount) => {
    if (!rolesAndPermissionStore.hasManagePermission("Edit the Application"))
      return;
    const response = await merchantAccountStore.updateApplicationState(
      item.id,
      ApplicationStatus.Inactive,
    );
    if (response.result) {
      ToastService.showSuccess("Update Successful");
      merchantAccountStore.fetchMerchantAccounts();
    }
  };

  const handleActiveApplication = async (item: MerchantAccount) => {
    if (!rolesAndPermissionStore.hasManagePermission("edit the application"))
      return;
    const response = await merchantAccountStore.updateApplicationState(
      item.id,
      ApplicationStatus.Active,
    );
    if (response.result) {
      ToastService.showSuccess("Update Successful");
      merchantAccountStore.fetchMerchantAccounts();
    }
  };

  const handleMerchantAccountAction = (
    item: MerchantAccount,
    actionType: "delete" | "cancel",
  ) => {
    if (
      !rolesAndPermissionStore.hasManagePermission(
        actionType === "cancel"
          ? "Cancel the application"
          : "Delete the application",
      )
    )
      return;
    const data = item.createAPIModel2();
    const apiModel: IAPIDeleteMerchantAccount = {
      accountId: AuthService.authData?.accountId || "",
      merchantAccount: data,
    };

    const actionService =
      actionType === "delete"
        ? MerchantAccountService.deleteMerchantAccount(apiModel)
        : MerchantAccountService.cancelMerchantAccount(apiModel);

    actionService.subscribe({
      next: (result) => {
        if (!result.result) {
          const errorMessage =
            actionType === "delete"
              ? "Error in deleting merchant account"
              : "Error in cancelling application";
          ToastService.showError(errorMessage);
        } else {
          merchantAccountStore.fetchMerchantAccounts();
          const successMessage =
            actionType === "delete"
              ? "Merchant Account deleted successfully"
              : "Application cancelled successfully";
          ToastService.showSuccess(successMessage);
        }
      },
      error: () => {
        const errorMessage =
          actionType === "delete"
            ? "Error in deleting merchant account"
            : "Error in cancelling application";
        ToastService.showError(errorMessage);
      },
    });
  };

  const getStatusStyles = (status: ApplicationStatus) => {
    switch (status) {
      case ApplicationStatus.Pending:
        return { backgroundColor: "#FFF5F8", color: "#DE5F85" };
      case ApplicationStatus.Active:
        return { backgroundColor: "#EEF7EA", color: "#2ECC71" };
      case ApplicationStatus.Inactive:
        return { backgroundColor: "#F9F9F9", color: "#95A5A6" };
      case "InReview" as keyof typeof ApplicationStatus:
        return { backgroundColor: "#ECF0FF", color: "#6A7AB5" };
      case ApplicationStatus.Cancelled:
        return { backgroundColor: "#F9F9F9", color: "#95A5A6" };
      default:
        return { backgroundColor: "#EEEEEE", color: "#555555" };
    }
  };

  const renderBadge = (
    statusStyles: { backgroundColor: string; color: string },
    applicationStatus: ApplicationStatus,
  ) => (
    <CustomButton
      bgColor={statusStyles.backgroundColor}
      color={statusStyles.color}
      fontWeight="600 !important"
    >
      {ApplicationStatus[applicationStatus as keyof typeof ApplicationStatus]}
    </CustomButton>
  );

  const renderDropdown = (item: MerchantAccount) => (
    <Dropdown align="end" className="merchant-status-button">
      <Dropdown.Toggle as="div" className="ellipsis-vertical">
        <img src={ellipsisVertical} />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {getMenuItems(item).map((menuItem, menuIndex) => (
          <Dropdown.Item key={menuIndex} onClick={() => menuItem.action(item)}>
            {menuItem.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );

  const onClickCardBody = (id: string) => {
    navigate(`/app/merchant-accounts/details/${id}`);
  };

  const renderCardBody = (
    item: MerchantAccount,
    statusStyles: { backgroundColor: string; color: string },
  ) => (
    <>
      <FlexContainer className="merchant-card-header">
        {renderBadge(statusStyles, item.applicationStatus)}
        <img src={item.getLogo()} className={item.getClassName()} />
        {renderDropdown(item)}
      </FlexContainer>
      <FlexContainer fxLayout="column" alignItems="center" spacing={20}>
        <FlexContainer
          fxLayout="column"
          alignItems="center"
          spacing={20}
          onClick={() => onClickCardBody(item.id)}
        >
          <CustomLabel textAlign="center" fontSize={"18px"} fontWeight="600">
            {item.name}
            {item.isDefault && (
              <img
                src={TickIcon}
                alt="Selected"
                style={{
                  height: "14px",
                  width: "14px",
                  marginLeft: "6px",
                  marginBottom: "2px",
                }}
              />
            )}
          </CustomLabel>
          <CustomLabel
            textAlign="center"
            fontSize={"14px"}
            className="merchant-card-descr"
          >
            {item.applicationStatus === ApplicationStatus.Active ||
            item.applicationStatus === ApplicationStatus.InReview ? (
              <>
                Your merchant account is approved and active.
                <br />
                You are able to process payments.
              </>
            ) : item.isFiservMerchantAccount() ? (
              <>
                Open the link below, complete all required information, sign
                electronically and submit.
              </>
            ) : (
              <>Your merchant account is connected but not yet active.</>
            )}
          </CustomLabel>
        </FlexContainer>
        {!(item.applicationStatus === ApplicationStatus.Pending) && (
          <CustomLabel>
            <span className="semi-bold">
              ${AccountSettingsStore.formatAmount(item.amountProcessed)}
            </span>
            <span className="charcoal-label"> processed since</span>
            <span className="semi-bold">
              {" "}
              {UtilityMethods.formatDateInString(new Date(item.createdDate))}
            </span>
          </CustomLabel>
        )}
        {item.applicationStatus === ApplicationStatus.Pending && (
          <FlexContainer
            justifyContent="center"
            alignItems="center"
            fxWrap="nowrap"
            spacing={12}
            padding="5px 0px 0px"
          >
            <RedButton
              height="33px !important"
              padding="6px 12px"
              minWidth="0"
              onClick={() => handleMerchantAccountAction(item, "cancel")}
            >
              Cancel Application
            </RedButton>
            {item.gateway === "Fiserv" && (
              <SapphireBlueButton
                onClick={() => handleCompleteApplication(item)}
                height="33px !important"
                padding="6px 12px"
                minWidth="0"
              >
                Complete Application
              </SapphireBlueButton>
            )}
          </FlexContainer>
        )}
      </FlexContainer>
    </>
  );

  if (merchantAccountStore.isMerchantAccountsLoadingInProgress) {
    return <>{new Loader(true).loaderComponent}</>;
  }
  if (merchantAccountsData.length === 0 && !isLoading) {
    return <Navigate to="/app/merchant-accounts/no-merchant" />;
  }
  return (
    <FlexContainer
      fxWrap="wrap"
      spacing={30}
      justifyContent={
        merchantAccountsData.length <= 2 ? "flex-start" : "center"
      }
      className={`${merchantAccountsData.length === 0 ? "full-height" : ""}`}
    >
      {showDeletePopup && merchantAccountToDelete && (
        <DeleteMerchantAccountPopUp
          onClose={(refresh: boolean) => onCloseDeletePopUp(refresh)}
          merchantAccount={merchantAccountToDelete}
        />
      )}
      {merchantAccountsData.map((item, index) => {
        const statusStyles = getStatusStyles(
          item.applicationStatus as ApplicationStatus,
        );
        return (
          <MerchantCard key={index} $fill={merchantAccountsData.length > 2}>
            {renderCardBody(item, statusStyles)}
          </MerchantCard>
        );
      })}
    </FlexContainer>
  );
};

export default withRouter(observer(MerchantAccounts));
